import type {
  AccountInfo,
  InflationReward,
  ParsedAccountData,
  TransactionInstruction,
} from '@solana/web3.js'
import { useAnchorWallet, useWallet } from '@solana/wallet-adapter-vue'
import {
  PublicKey,
  StakeProgram,
} from '@solana/web3.js'
import { defineStore } from 'pinia'
import { Notify } from 'quasar'
import { computed, ref, watch } from 'vue'
import { withPriorityFees } from '~/feautures/priority-fee'
import { useApp } from '~/hooks/app'
import { useDeposit } from '~/hooks/deposit'
import { useMonitorTransaction } from '~/hooks/monitor'
import { useConnectionStore } from '~/store/connection'
import { useEpochStore } from '~/store/epoch'
import { addToDelegators, checkWalletInDelegators, getFilteredProgramAccounts, getWalletRewards, lamportsToSol, sendTransaction, sleep } from '~/utils'

export type EpochStakesRewards = {
  epoch: number
  stakeSum: number
  commission: number
  solPrice: number
}

export type ProgramAccount = {
  pubkey: PublicKey
  account: AccountInfo<ParsedAccountData>
}

export type StakeAccount = ProgramAccount & {
  rewards: InflationReward[]
}

export type StakeAccountWithState = {
  stakeAccount: StakeAccount
  state: string
}

export const DEACTIVATE_ACTION = 'Deactivate'
export const ACTIVATE_ACTION = 'Activate'
export const WITHDRAW_ACTION = 'Withdraw'
export const MERGE_ACTION = 'Merge Accounts'

function calcStakeBalance(accounts: ProgramAccount[]) {
  const lamports = accounts.reduce((total, acc) => {
    total += acc.account.lamports
    return total
  }, 0)
  return lamportsToSol(lamports)
}

export const useStakeAccountStore = defineStore('stake-accounts', () => {
  const { monitorTransaction } = useMonitorTransaction()
  const deposit = useDeposit()
  const connectionStore = useConnectionStore()
  const { publicKey } = useWallet()

  const anchorWallet = useAnchorWallet()
  const { isClient } = useApp()

  const data = ref<StakeAccount[]>([])
  const dataSelected = ref<string[]>([])
  const updatingRewards = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const retrieveDataReq = ref<boolean>(false)
  const stakesOnServer = ref<boolean>(false)
  const hasAllData = ref<boolean>(true)
  const checkInProgress = ref<boolean>(false)
  const stateLoaded = ref<boolean>(false)

  const epochStore = useEpochStore()
  const epoch = computed(() => epochStore.epochNumber)

  const loadingPubkeys = reactive<Set<string>>(new Set())

  const accountsFull = ref<StakeAccountWithState[]>([])

  const stakeDialog = ref(false)

  const checkStakesStatus = async () => {
    if (!checkInProgress.value) {
      // TODO: fix this endpoint on server
      return hasAllData.value = true
      // checkInProgress.value = true
      // hasAllData.value = await getStakesStatus(`${publicKey.value}`)
      // checkInProgress.value = false
    }
  }

  if (!import.meta.env.SSR) {
    setInterval(async () => {
      if (stakesOnServer.value && !retrieveDataReq.value) {
        await checkStakesStatus()
      }
    }, 60_000)
  }

  function removeAccount(address: string) {
    data.value = data.value.filter(acc => acc.pubkey.toBase58() !== address)
  }

  const getInflationRewards = async (keys: PublicKey[], epoch: number): Promise<(InflationReward | null)[]> => {
    try {
      return await connectionStore.connection.getInflationReward(keys, epoch)
    } catch (error) {
      console.log('getInflationRewards error =', error)
      await sleep(500)
      return getInflationRewards(keys, epoch)
    }
  }

  const updateRewards = async () => {
    if (updatingRewards.value || !publicKey.value) {
      return
    }

    const newAccountsFull = data.value.map((item) => {
      return {
        ...item,
      }
    })

    const startEpoch = epoch.value - 1
    if (startEpoch === -1 || newAccountsFull.length === 0) {
      return
    }

    updatingRewards.value = true

    stakesOnServer.value = (await checkWalletInDelegators(`${publicKey.value}`)).length > 0
    stateLoaded.value = true

    if (stakesOnServer.value) {
      const rewards = await getWalletRewards(`${publicKey.value}`)

      for (const acc of newAccountsFull) {
        const accPubKey = acc.pubkey.toBase58()
        acc.rewards = rewards
          .filter((reward: { publicKey: string }) => reward.publicKey === accPubKey)
          .sort((a: { epoch: number }, b: { epoch: number }) => b.epoch - a.epoch)
      }
    } else {
      await addWalletToDelegators()
      for (const acc of newAccountsFull) {
        acc.rewards = []
      }

      const pubkeys = newAccountsFull.map(acc => acc.pubkey)
      for (let epoch = startEpoch; epoch > startEpoch - 5; epoch--) {
        const rewards = await getInflationRewards(pubkeys, epoch)
        // console.log(`getInflationRewards ${epoch} == `, rewards);
        for (const [index, reward] of rewards.entries()) {
          if (reward) {
            newAccountsFull[index]?.rewards.push(reward)
          }
        }
      }

      setTimeout(updateRewards, 5000)
    }
    for (const acc of newAccountsFull) {
      acc.rewards = acc.rewards.filter(
        reward =>
          reward.epoch >= (acc.account.data?.parsed?.info.stake?.delegation.activationEpoch ?? 0),
      )
    }

    data.value = newAccountsFull
    updatingRewards.value = false
  }

  if (!import.meta.env.SSR) {
    setInterval(() => {
      if (stakesOnServer.value && !hasAllData.value && !retrieveDataReq.value) {
        Promise.all([updateRewards(), checkStakesStatus()]).then()
      }
    }, 5000)
  }

  watch(epoch, (newEpoch, oldEpoch) => {
    if (oldEpoch < 1 && newEpoch >= 1) {
      updateRewards().then()
    }
  })

  const updateAccounts = async (pubkeys?: PublicKey[]) => {
    const source = pubkeys ? accountsFull.value : data.value
    const newData = pubkeys ? await loadStakeAccountsByPubkeys(pubkeys) : []
    accountsFull.value = source.map((item: any) => {
      // if used accountsFull then acc = item.stakeAccount
      const acc = pubkeys ? item.stakeAccount : item
      let state = ''
      try {
        const newDataIndex = pubkeys ? pubkeys.findIndex(pubkey => pubkey.equals(acc.pubkey)) : -1
        const stakeNewData = newDataIndex >= 0
          ? newData[newDataIndex]
          : acc.account

        if (!stakeNewData.state && epoch.value > 0) {
          const delegation = stakeNewData.data.parsed?.info.stake?.delegation
          if (delegation) {
            const activation = +delegation.activationEpoch
            const deactivation = +delegation.deactivationEpoch
            // state: 'active' | 'inactive' | 'activating' | 'deactivating';
            if (activation < epoch.value && deactivation > 99_999) {
              stakeNewData.state = 'active'
            } else if (activation === epoch.value && deactivation > 99_999) {
              stakeNewData.state = 'activating'
            } else if (activation < epoch.value && deactivation === epoch.value) {
              stakeNewData.state = 'deactivating'
            } else if ((activation < epoch.value && deactivation < epoch.value) || (activation === epoch.value && deactivation === epoch.value)) {
              stakeNewData.state = 'inactive'
            }
          }
        }
        state = stakeNewData.state
      } catch (error) {
        console.log('getStakeActivation error ===', error)
      }
      return {
        stakeAccount: acc,
        state,
      }
    })
  }

  watch(data, () => updateAccounts(), { immediate: true })

  if (!import.meta.env.SSR) {
    setInterval(() => {
      if (loading.value) {
        return
      }
      const accountsToUpdate: PublicKey[] = []
      for (const acc of accountsFull.value) {
        if (!acc.state && !loadingPubkeys.has(acc.stakeAccount.pubkey.toBase58())) {
          accountsToUpdate.push(acc.stakeAccount.pubkey)
        }
      }
      if (accountsToUpdate.length > 0) {
        updateAccounts(accountsToUpdate).then()
      }
    }, 10_000)
  }

  async function loadStakeAccounts() {
    if (!publicKey.value) {
      console.log('[Stake accounts] Skip loading...')
      return
    }

    // stake user info account
    const filters = [
      {
        // 12 is Staker authority offset in stake account stake
        memcmp: {
          offset: 12 + 32,
          bytes: publicKey.value.toBase58(),
          // bytes: '2n5KTrz1b5MLkPATsosHJhc9JsRtnMxAEq1mWHZiHsZR',
        },
      },
      { dataSize: 200 },
      // {
      //   memcmp: {
      //     offset: 44,
      //     bytes: publicKey!.toBase58(),
      //   },
      // },
      // {
      //   dataSize: USER_STAKE_INFO_ACCOUNT_LAYOUT.span,
      // },
    ]

    return await getFilteredProgramAccounts(
      connectionStore.connection,
      StakeProgram.programId,
      filters,
    )
  }

  async function loadStakeAccountsByPubkeys(pubkeys: PublicKey[]) {
    const data = await connectionStore.connection.getMultipleParsedAccounts(pubkeys)
    return data.value
  }

  async function load() {
    if (!isClient.value) {
      return
    }
    if (loading.value || !publicKey.value) {
      console.log('[Stake accounts] Skip loading...')
      return
    }
    console.log('[Stake accounts] Loading...')
    loading.value = true

    try {
      // @ts-expect-error ...
      data.value = await loadStakeAccounts()
      // console.log('[Stake accounts] Data:', data.value);
      // console.log(data.value.map((acc) => acc.pubkey.toBase58()).join('\n'));
      await updateRewards()
    } catch {
      Notify.create({
        message: `Failed to retrieve staking accounts. Please try again later, or select another RPC server and retry`,
        type: 'negative',
        position: 'bottom',
        multiLine: true,
        timeout: 0,
        textColor: 'black',
        actions: [
          {
            label: 'Retry',
            color: 'white',
            handler: load,
          },
          {
            label: 'Dismiss',
            color: 'white',
          },
        ],
      })
    } finally {
      loading.value = false
    }
  }

  async function addWalletToDelegators() {
    try {
      if (!publicKey.value || retrieveDataReq.value || stakesOnServer.value) {
        return
      }
      retrieveDataReq.value = true
      await addToDelegators(`${publicKey.value}`)
      stakesOnServer.value = true
    } finally {
      retrieveDataReq.value = false
    }
  }

  watch(
    publicKey,
    async (newWallet, oldWallet) => {
      await addWalletToDelegators()
      loading.value = false
      if (!newWallet || newWallet?.toBase58() !== oldWallet?.toBase58()) {
        stateLoaded.value = false
        data.value = []
      }
      await load()
    },
    { immediate: true },
  )

  const deactivate = async (addresses: string[]) => {
    const instructions: TransactionInstruction[] = []
    const stakePubkeys: PublicKey[] = []

    for (const address of addresses) {
      loadingPubkeys.add(address)
      const stakePubkey = new PublicKey(address)
      stakePubkeys.push(stakePubkey)

      const instruction = StakeProgram.deactivate({
        stakePubkey,
        authorizedPubkey: publicKey.value!,
      }).instructions

      instructions.push(...instruction)
    }

    await monitorTransaction(
      sendTransaction(connectionStore.connection, anchorWallet.value!, await withPriorityFees({ instructions }), []),
      {
        commitment: 'finalized',
      },
    )

    await updateAccounts(stakePubkeys)

    for (const address of addresses) {
      loadingPubkeys.delete(address)
    }
  }

  const withdraw = async (stakes: { address: string, lamports?: number }[]) => {
    const instructions: TransactionInstruction[] = []
    // const stakePubkeys: PublicKey[] = []

    for (const stake of stakes) {
      loadingPubkeys.add(stake.address)
      const stakePubkey = new PublicKey(stake.address)
      let lamports = stake.lamports ?? 0
      if (!lamports) {
        const acc = data.value.find(item => item.pubkey.equals(stakePubkey))
        if (acc) {
          lamports = acc.account.lamports
        }
      }

      const instruction = StakeProgram.withdraw({
        stakePubkey,
        authorizedPubkey: publicKey.value!,
        toPubkey: publicKey.value!,
        lamports,
      }).instructions

      instructions.push(...instruction)
    }

    await monitorTransaction(
      sendTransaction(connectionStore.connection, anchorWallet.value!, await withPriorityFees({ instructions }), []),
      {
        commitment: 'finalized',
        onSuccess: () => {
          for (const stake of stakes) {
            removeAccount(stake.address)
          }
        },
      },
    )
    for (const stake of stakes) {
      loadingPubkeys.delete(stake.address)
    }
  }

  const activate = async (addresses: string[], vote: string) => {
    const stakePubkeys: PublicKey[] = []
    for (const address of addresses) {
      loadingPubkeys.add(address)
      const stakePubkey = new PublicKey(address)
      stakePubkeys.push(stakePubkey)
    }

    await deposit.delegateAccount(stakePubkeys, vote)
    await updateAccounts(stakePubkeys)

    for (const address of addresses) {
      loadingPubkeys.delete(address)
    }
  }

  const stakeSolBalance = computed(() => calcStakeBalance(data.value))

  return {
    loading,
    stateLoaded,
    updatingRewards,
    data,
    dataSelected,
    accountsFull,
    retrieveDataReq,
    loadingPubkeys,
    stakesOnServer,
    hasAllData,
    stakeDialog,
    load,

    stakeSolBalance,

    removeAccount,
    activate,

    deactivate,

    withdraw,
  }
})
